import {openLogDrawer} from '@/modules/system-logs/components/log-drawer/util'
import {toDayKey} from '@/modules/system-logs/functions'
import {SystemLogsColumnsDef} from '@/modules/system-logs/table-columns'
import {InfiniteDataTableV2, type InfiniteDataTableV2Props} from '@cypro/ui/components/ui/infinite-data-table'
import {getMonthDayFormatter} from '@cypro/ui/utils'
import {EventLog} from '@cypro/engine/schema'
import {chakra} from '@chakra-ui/react'
import {useTranslate} from '@tolgee/react'
import {useCallback, useMemo} from 'react'
import {LuBotMessageSquare} from 'react-icons/lu'

// oxlint-disable-next-line typescript/no-empty-object-type
type Props<TStartRow> = Omit<InfiniteDataTableV2Props<EventLog, TStartRow>, 'emptyState'> & {
  /** For a list of one patient's logs, where repeating the patient on every row is noise. */
  hidePatient?: boolean
}

export function SystemLogsTable<TStartRow>({
  table: tableConfig,
  virtualizer: virtualizerConfig,
  hidePatient = false,
  ...rest
}: Props<TStartRow>) {
  const {t} = useTranslate('common')

  const columns = useMemo(
    () => [
      SystemLogsColumnsDef.dayKeyColumn(),
      SystemLogsColumnsDef.userAction(),
      SystemLogsColumnsDef.domainColumn(undefined, {hidePatient}),
      SystemLogsColumnsDef.statusColumn(),
    ],
    [hidePatient]
  )

  const isHeaderRow = useCallback(
    (current: EventLog, previous: EventLog | undefined) => {
      if (!previous) return true
      return toDayKey(current.performedAt) !== toDayKey(previous.performedAt)
    },
    []
  )

  const renderHeader = useCallback(
    (rowData: EventLog) => (
      <chakra.div
        display="flex"
        width="100%"
        height="100%"
        alignItems="center"
        gap="10px"
        px={3}
      >
        <chakra.span fontSize="sm" whiteSpace="nowrap">
          {getMonthDayFormatter().format(rowData.performedAt)}
        </chakra.span>
        <chakra.div
          width="100%"
          border="solid"
          borderWidth="1px"
          height="0px"
          borderColor="border"
        />
      </chakra.div>
    ),
    []
  )

  return (
    <InfiniteDataTableV2<EventLog, TStartRow>
      {...rest}
      table={{
        ...tableConfig,
        columns,
      }}
      virtualizer={virtualizerConfig}
      isHeaderRow={isHeaderRow}
      renderHeader={renderHeader}
      hideHeader
      emptyState={{
        title: t('no_system_events'),
        description: '',
        icon: LuBotMessageSquare,
      }}
      onRowClick={openLogDrawer}
    />
  )
}
